import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Steps,
  Tag,
  Typography,
  notification,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ONBOARDING_STAGE_KEYS,
  PartnerOnboardingStages,
  PartnerOnboardingStatuses,
  partnerTypeLabel,
  allOnboardingTaskTemplatesQuery,
  extractAllOnboardingTaskTemplates,
  extractPartnerOnboarding,
  partnerOnboardingQuery,
  cancelPartnerOnboardingMutation,
  reopenPartnerOnboardingMutation,
  submitOnboardingTaskResponseMutation,
  updateOnboardingTaskNotesMutation,
} from '../constants';
import EditPartnerOnboardingModal from '../EditPartnerOnboardingModal';
import TaskResponseItem from './TaskResponseItem';

const { Title, Text } = Typography;
const { Step } = Steps;

function ViewPartnerOnboardingModal({ _id, visible, onCancel, onSuccess, onDelete, deleting }) {
  const { data, loading, refetch } = useQuery(partnerOnboardingQuery, {
    variables: { _id },
    skip: !_id,
    fetchPolicy: 'network-only',
  });
  const record = extractPartnerOnboarding(data);

  const { data: templatesData } = useQuery(allOnboardingTaskTemplatesQuery, {
    variables: { first: 200, filters: { isActive: true } },
    skip: !visible,
    fetchPolicy: 'cache-first',
  });
  const templates = useMemo(() => {
    const templatesEdges = extractAllOnboardingTaskTemplates(templatesData);
    return (templatesEdges?.edges || []).map((e) => e.node);
  }, [templatesData]);

  // Closes the modal on the way out, so the contact page isn't left behind an open
  // dialog. onClick on the Link rather than history.push keeps real anchor semantics.
  const contactLink = useCallback(
    (contactId, name, canView) => {
      if (!canView || !contactId) return name;
      return (
        <Link to={`/contact/view/${contactId}/partner-onboarding`} onClick={onCancel}>
          {name}
        </Link>
      );
    },
    [onCancel],
  );

  const [submitResponse] = useMutation(submitOnboardingTaskResponseMutation);
  const [updateTaskNotes] = useMutation(updateOnboardingTaskNotesMutation);
  const [reopenOnboarding, { loading: reopening }] = useMutation(reopenPartnerOnboardingMutation);
  const [cancelOnboarding, { loading: cancelling }] = useMutation(cancelPartnerOnboardingMutation);
  const [editing, setEditing] = useState(false);
  const [activeStage, setActiveStage] = useState(1);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const stageInitialized = useRef(false);

  // The modal stays mounted across records on the list page — reset the stage view
  // when it's pointed at a different onboarding.
  useEffect(() => {
    stageInitialized.current = false;
    setActiveStage(1);
  }, [_id]);

  const stageTasks = useMemo(
    () =>
      templates.filter(
        (t) =>
          t.stageNumber === activeStage &&
          (!t.partnerTypeCodes?.length || t.partnerTypeCodes.includes(record?.partnerType)),
      ),
    [templates, activeStage, record],
  );

  const responseMap = useMemo(
    () => Object.fromEntries((record?.taskResponses || []).map((r) => [r.templateId, r])),
    [record],
  );

  // A stage with every applicable task answered renders as `finish` rather than `wait`,
  // so a completed Stage 1 doesn't look identical to an untouched Stage 4. A stage with
  // no applicable tasks is not "finished" -- it just doesn't apply to this partner type.
  const completedStages = useMemo(() => {
    const applicable = templates.filter(
      (t) => !t.partnerTypeCodes?.length || t.partnerTypeCodes.includes(record?.partnerType),
    );
    return new Set(
      ONBOARDING_STAGE_KEYS.filter((s) => {
        const tasksForStage = applicable.filter((t) => t.stageNumber === s);
        return (
          tasksForStage.length > 0 &&
          tasksForStage.every((t) => responseMap[t._id]?.completedAt)
        );
      }),
    );
  }, [templates, record, responseMap]);

  // Open on the current stage (earliest with an incomplete task), not always stage 1.
  useEffect(() => {
    if (stageInitialized.current || !record || !templates.length) return;
    const applicable = templates.filter(
      (t) => !t.partnerTypeCodes?.length || t.partnerTypeCodes.includes(record.partnerType),
    );
    const firstIncomplete = ONBOARDING_STAGE_KEYS.find((s) =>
      applicable.some((t) => t.stageNumber === s && !responseMap[t._id]?.completedAt),
    );
    stageInitialized.current = true;
    if (firstIncomplete) setActiveStage(firstIncomplete);
  }, [record, templates, responseMap]);

  const answeredCount = stageTasks.filter((t) => responseMap[t._id]?.answer).length;
  const statusInfo =
    PartnerOnboardingStatuses[record?.status] || PartnerOnboardingStatuses.IN_PROGRESS;
  const tasksDone = record?.tasksDoneCount ?? 0;
  const tasksTotal = record?.tasksTotalCount ?? 0;
  const isComplete = record?.status === 'COMPLETE';
  const isCancelled = record?.status === 'CANCELLED';
  const reopenHistory = record?.reopenHistory || [];
  const lastReopen = reopenHistory[reopenHistory.length - 1];

  // Mutations return the full record — Apollo's normalized cache updates the view.
  const handleSubmitResponse = useCallback(
    async (templateId, answer) => {
      try {
        await submitResponse({ variables: { input: { _id, templateId, answer } } });
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [submitResponse, _id],
  );

  const handleUpdateNotes = useCallback(
    async (templateId, notes) => {
      try {
        await updateTaskNotes({ variables: { input: { _id, templateId, notes } } });
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [updateTaskNotes, _id],
  );

  const handleReopen = useCallback(async () => {
    try {
      await reopenOnboarding({ variables: { input: { _id, reason: reopenReason } } });
      notification.success({ message: 'Onboarding reopened' });
      setReopenOpen(false);
      setReopenReason('');
      await refetch();
    } catch (err) {
      notification.error({ message: 'Error', description: err.message });
    }
  }, [reopenOnboarding, _id, reopenReason, refetch]);

  const handleCancel = useCallback(async () => {
    try {
      await cancelOnboarding({ variables: { input: { _id, reason: cancelReason } } });
      notification.success({ message: 'Onboarding cancelled' });
      setCancelOpen(false);
      setCancelReason('');
      await refetch();
    } catch (err) {
      notification.error({ message: 'Error', description: err.message });
    }
  }, [cancelOnboarding, _id, cancelReason, refetch]);

  if (!record && !loading) return null;

  return (
    <>
      <Modal
        open={visible}
        onCancel={onCancel}
        title="Partner Onboarding"
        width={800}
        footer={null}
        destroyOnClose
      >
        {record && (
          <>
            {isCancelled && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Onboarding cancelled"
                description={
                  <span>
                    {record.cancellationReason ? `“${record.cancellationReason}”` : null}
                    {record.onboardingCancelledDate
                      ? ` — ${new Date(record.onboardingCancelledDate).toLocaleString()}`
                      : null}
                  </span>
                }
              />
            )}
            {isComplete && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                message="Onboarding complete — checklist locked"
                description="Reopen it to make changes (a reason is required)."
                action={
                  <Button size="small" onClick={() => setReopenOpen(true)}>
                    Reopen
                  </Button>
                }
              />
            )}
            {lastReopen && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Reopened ${reopenHistory.length} time${reopenHistory.length > 1 ? 's' : ''}`}
                description={
                  <span>
                    Last: {new Date(lastReopen.reopenedAt).toLocaleString()} — “{lastReopen.reason}”
                  </span>
                }
              />
            )}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Region">
                {record.regionName || record.region}
              </Descriptions.Item>
              <Descriptions.Item label="Contact" span={2}>
                {record.contact?.fullName ? (
                  contactLink(record.contactId, record.contact.fullName, record.canViewContact)
                ) : (
                  <Text copyable>{record.contactId}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Partner Type">
                {partnerTypeLabel(record.partnerType) || '—'}
              </Descriptions.Item>
              {record.coordinatorContactId && (
                <Descriptions.Item label="Coordinator" span={2}>
                  {record.coordinatorContact?.fullName
                    ? contactLink(
                        record.coordinatorContactId,
                        record.coordinatorContact.fullName,
                        record.canViewCoordinatorContact,
                      )
                    : record.coordinatorContactId}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Progress">
                <Progress
                  percent={tasksTotal ? Math.round((tasksDone / tasksTotal) * 100) : 0}
                  size="small"
                  format={() => `${tasksDone}/${tasksTotal}`}
                  status={record.status === 'COMPLETE' ? 'success' : 'normal'}
                  style={{ maxWidth: 220 }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {record.updatedAt ? new Date(record.updatedAt).toLocaleString() : ''}
              </Descriptions.Item>
              {record.assignedStaffIds?.length > 0 && (
                <Descriptions.Item label="Assigned Staff" span={2}>
                  {record.assignedStaffIds
                    .map(
                      (staffId) =>
                        record.assignedStaff?.find((u) => u._id === staffId)?.name || staffId,
                    )
                    .join(', ')}
                </Descriptions.Item>
              )}
              {record.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {record.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Steps
              current={activeStage - 1}
              onChange={(idx) => setActiveStage(idx + 1)}
              size="small"
              style={{ marginBottom: 24, cursor: 'pointer' }}
            >
              {ONBOARDING_STAGE_KEYS.map((s) => (
                <Step
                  key={s}
                  title={PartnerOnboardingStages[s]?.label}
                  status={
                    activeStage === s
                      ? 'process'
                      : completedStages.has(s)
                      ? 'finish'
                      : 'wait'
                  }
                />
              ))}
            </Steps>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {PartnerOnboardingStages[activeStage]?.label} Tasks
              </Title>
              {stageTasks.length > 0 && (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {answeredCount} / {stageTasks.length} answered
                </Text>
              )}
            </div>

            {stageTasks.length === 0 && (
              <Text type="secondary">No tasks defined for this stage.</Text>
            )}

            {stageTasks.map((template) => (
              <TaskResponseItem
                key={`${record._id}:${template._id}`}
                template={template}
                response={responseMap[template._id]}
                onSubmit={handleSubmitResponse}
                onUpdateNotes={handleUpdateNotes}
                isReadOnly={template.assignedRole === 'BOT' || isComplete || isCancelled}
                contactName={record.contact?.fullName}
                contactId={record.contactId}
                partnerType={record.partnerType}
                onDataChanged={refetch}
                defaultAnswer="No"
              />
            ))}

            <Divider />

            <Space>
              <Button onClick={() => setEditing(true)}>Edit</Button>
              {record.status === 'IN_PROGRESS' && (
                <Button danger onClick={() => setCancelOpen(true)}>
                  Cancel Onboarding
                </Button>
              )}
              <Popconfirm
                title="Delete this onboarding record?"
                onConfirm={() => onDelete && onDelete(record._id)}
                okText="Delete"
                okType="danger"
              >
                <Button danger loading={deleting === record._id}>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </>
        )}
      </Modal>
      <Modal
        open={reopenOpen}
        title="Reopen onboarding"
        okText="Reopen"
        okButtonProps={{ disabled: !reopenReason.trim(), loading: reopening }}
        onOk={handleReopen}
        onCancel={() => setReopenOpen(false)}
        destroyOnClose
      >
        <Text type="secondary">
          This sets the onboarding back to In Progress and records who reopened it and why.
        </Text>
        <Input.TextArea
          rows={3}
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder="Why are you reopening this onboarding? (required)"
          style={{ marginTop: 8 }}
        />
      </Modal>
      <Modal
        open={cancelOpen}
        title="Cancel onboarding"
        okText="Cancel onboarding"
        okType="danger"
        okButtonProps={{ disabled: !cancelReason.trim(), loading: cancelling }}
        cancelText="Keep onboarding"
        onOk={handleCancel}
        onCancel={() => setCancelOpen(false)}
        destroyOnClose
      >
        <Text type="secondary">
          This stops the onboarding and records the reason. To onboard this contact
          again later, start a new onboarding from their profile (this record is kept
          for history).
        </Text>
        <Input.TextArea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Why are you cancelling this onboarding? (required)"
          style={{ marginTop: 8 }}
        />
      </Modal>
      <EditPartnerOnboardingModal
        record={record}
        visible={editing}
        onCancel={() => setEditing(false)}
        onSuccess={async () => {
          setEditing(false);
          await refetch();
        }}
      />
    </>
  );
}

export default ViewPartnerOnboardingModal;
